<script setup>
/* global axios */
// Cadastro em MASSA de templates WhatsApp (porta do KanbanCW): monta o template
// uma vez (cabeçalho/corpo/rodapé/botões + preview) e cria em VÁRIAS caixas
// oficiais (Cloud API) selecionadas de uma vez. Resultado por caixa.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import KanbanSelect from './KanbanSelect.vue';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const apiBase = `/api/v1/accounts/${accountId}/kanban`;
const base = `${apiBase}/whatsapp_templates`;
// endpoint PADRÃO de inboxes (traz channel_type + provider, ao contrário do
// /kanban/inboxes que devolve só {id,name}).
const inboxesUrl = `/api/v1/accounts/${accountId}/inboxes`;

const inboxes = ref([]);
const selected = ref(new Set());
const results = ref([]);
const saving = ref(false);
const error = ref('');

const officialInboxes = computed(() =>
  inboxes.value.filter(
    i => i.channel_type === 'Channel::Whatsapp' && i.provider === 'whatsapp_cloud'
  )
);
const toggleInbox = id => {
  const s = new Set(selected.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  selected.value = s;
};
const allSelected = computed(
  () =>
    officialInboxes.value.length > 0 &&
    officialInboxes.value.every(i => selected.value.has(i.id))
);
const toggleAll = () => {
  selected.value = allSelected.value
    ? new Set()
    : new Set(officialInboxes.value.map(i => i.id));
};

const LANGUAGES = ['pt_BR', 'en_US', 'es_ES', 'en', 'es'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_FORMATS = ['none', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
const ACCEPT_BY_FORMAT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

const freshForm = () => ({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  header_format: 'none',
  header_text: '',
  header_handle: '',
  header_media_name: '',
  header_media_preview: '',
  body: '',
  footer: '',
  buttons: [],
});
const form = ref(freshForm());
const bodyVarExamples = ref({});
const headerVarExamples = ref({});

const extractVarIndices = text => {
  const matches = `${text || ''}`.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10)))].sort(
    (a, b) => a - b
  );
};
const bodyVarIndices = computed(() => extractVarIndices(form.value.body));
const headerVarIndices = computed(() =>
  form.value.header_format === 'TEXT' ? extractVarIndices(form.value.header_text) : []
);
// monta "{{n}}" em JS — não dá pra escrever literal no template (o Vue interpreta
// o }} interno como fim da interpolação).
const varTag = idx => `{{${idx}}}`;
const insertBodyVar = () => {
  const next = (bodyVarIndices.value[bodyVarIndices.value.length - 1] || 0) + 1;
  form.value.body = `${form.value.body || ''}{{${next}}}`;
};
const fillVars = (text, examples) =>
  `${text || ''}`.replace(
    /\{\{(\d+)\}\}/g,
    (m, n) => examples[n] || examples[Number(n)] || m
  );
const previewBody = computed(() => fillVars(form.value.body, bodyVarExamples.value));
const previewHeader = computed(() =>
  fillVars(form.value.header_text, headerVarExamples.value)
);

const isMediaHeader = computed(() =>
  MEDIA_HEADER_FORMATS.includes(form.value.header_format)
);

// opções p/ os KanbanSelect (dropdown custom, no lugar do <select> nativo)
const categoryOptions = CATEGORIES.map(c => ({ value: c, label: c }));
const languageOptions = LANGUAGES.map(l => ({ value: l, label: l }));
const buttonTypeOptions = BUTTON_TYPES.map(b => ({ value: b, label: b }));
const headerOptions = computed(() =>
  HEADER_FORMATS.map(h => ({ value: h, label: t(`DISPARADOR.templates.headerFmt.${h}`) }))
);
const uploadingMedia = ref(false);
const mediaError = ref('');
const uploadHeaderMedia = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  const firstInbox = [...selected.value][0];
  if (!firstInbox) {
    mediaError.value = t('DISPARADOR.templates.selectInboxForMedia');
    return;
  }
  uploadingMedia.value = true;
  mediaError.value = '';
  form.value.header_handle = '';
  form.value.header_media_name = file.name;
  form.value.header_media_preview =
    form.value.header_format === 'IMAGE' ? URL.createObjectURL(file) : '';
  try {
    const fd = new FormData();
    fd.append('inbox_id', firstInbox);
    fd.append('file', file);
    const { data } = await axios.post(`${base}/upload_header_media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    form.value.header_handle = data.handle;
  } catch (e) {
    mediaError.value = e?.response?.data?.error || t('DISPARADOR.templates.mediaError');
    form.value.header_media_name = '';
    form.value.header_media_preview = '';
  } finally {
    uploadingMedia.value = false;
  }
};

const addButton = () => {
  if (form.value.buttons.length >= 3) return;
  form.value.buttons.push({ type: 'QUICK_REPLY', text: '', url: '', phone_number: '' });
};
const removeButton = i => form.value.buttons.splice(i, 1);

const submit = async () => {
  error.value = '';
  results.value = [];
  if (selected.value.size === 0) {
    error.value = t('DISPARADOR.templates.selectAtLeastOne');
    return;
  }
  if (!form.value.name.trim() || !form.value.body.trim()) {
    error.value = t('DISPARADOR.templates.required');
    return;
  }
  if (isMediaHeader.value && !form.value.header_handle) {
    error.value = t('DISPARADOR.templates.mediaRequired');
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.post(`${base}/bulk_create`, {
      inbox_ids: [...selected.value],
      ...form.value,
      body_examples: bodyVarExamples.value,
      header_examples: headerVarExamples.value,
    });
    results.value = data.data || [];
  } catch (e) {
    error.value = e?.response?.data?.error || t('DISPARADOR.templates.error');
  } finally {
    saving.value = false;
  }
};

const resetForm = () => {
  form.value = freshForm();
  bodyVarExamples.value = {};
  headerVarExamples.value = {};
  results.value = [];
  error.value = '';
};

onMounted(async () => {
  try {
    inboxes.value = (await axios.get(inboxesUrl)).data.payload || [];
  } catch (e) {
    inboxes.value = [];
  }
});
</script>

<template>
  <div class="p-6 mx-auto max-w-5xl">
    <p class="mb-4 text-sm text-n-slate-11">
      {{ t('DISPARADOR.templates.subtitle') }}
    </p>

    <div v-if="!officialInboxes.length" class="py-8 text-sm text-center text-n-slate-10">
      {{ t('DISPARADOR.templates.noOfficial') }}
    </div>

    <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <!-- coluna form -->
      <div class="space-y-4 lg:col-span-2">
        <!-- caixas -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.inboxes')
            }}</label>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="toggleAll"
            >
              {{ allSelected ? t('DISPARADOR.templates.none') : t('DISPARADOR.templates.all') }}
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="ib in officialInboxes"
              :key="ib.id"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border cursor-pointer"
              :class="
                selected.has(ib.id)
                  ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
            >
              <input
                type="checkbox"
                :checked="selected.has(ib.id)"
                @change="toggleInbox(ib.id)"
              />
              {{ ib.name }}
            </label>
          </div>
        </div>

        <!-- nome / categoria / idioma -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.name')
            }}</label>
            <input
              v-model="form.name"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              placeholder="ex: promo_natal"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.category')
            }}</label>
            <KanbanSelect v-model="form.category" :options="categoryOptions" />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.language')
            }}</label>
            <KanbanSelect v-model="form.language" :options="languageOptions" />
          </div>
        </div>

        <!-- cabeçalho -->
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.templates.header')
          }}</label>
          <div class="mb-2">
            <KanbanSelect v-model="form.header_format" :options="headerOptions" />
          </div>
          <input
            v-if="form.header_format === 'TEXT'"
            v-model="form.header_text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.headerText')"
          />
          <div v-else-if="isMediaHeader">
            <input
              type="file"
              :accept="ACCEPT_BY_FORMAT[form.header_format]"
              @change="uploadHeaderMedia"
            />
            <span v-if="uploadingMedia" class="ml-2 text-xs text-n-slate-10">{{
              t('DISPARADOR.templates.uploading')
            }}</span>
            <span
              v-else-if="form.header_handle"
              class="ml-2 text-xs text-n-teal-11"
              >✓ {{ form.header_media_name }}</span
            >
            <p v-if="mediaError" class="mt-1 text-xs text-n-ruby-11">{{ mediaError }}</p>
            <p class="mt-1 text-[0.6875rem] text-n-slate-10">
              {{ t('DISPARADOR.templates.mediaHint') }}
            </p>
          </div>
        </div>

        <!-- corpo -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.body')
            }}</label>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="insertBodyVar"
            >
              + {{ t('DISPARADOR.templates.addVar') }}
            </button>
          </div>
          <textarea
            v-model="form.body"
            rows="4"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.bodyPlaceholder')"
          />
          <div v-if="bodyVarIndices.length" class="mt-2 space-y-1">
            <div
              v-for="i in bodyVarIndices"
              :key="`b${i}`"
              class="flex items-center gap-2"
            >
              <span class="text-xs text-n-slate-10 w-14">{{ varTag(i) }}</span>
              <input
                v-model="bodyVarExamples[i]"
                class="flex-1 px-2 py-1 text-xs rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
                :placeholder="t('DISPARADOR.templates.example')"
              />
            </div>
          </div>
        </div>

        <!-- rodapé -->
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.templates.footer')
          }}</label>
          <input
            v-model="form.footer"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            :placeholder="t('DISPARADOR.templates.footerPlaceholder')"
          />
        </div>

        <!-- botões -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.templates.buttons')
            }}</label>
            <button
              v-if="form.buttons.length < 3"
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="addButton"
            >
              + {{ t('DISPARADOR.templates.addButton') }}
            </button>
          </div>
          <div
            v-for="(btn, i) in form.buttons"
            :key="`btn${i}`"
            class="flex flex-wrap items-center gap-2 mb-2"
          >
            <KanbanSelect
              v-model="btn.type"
              :options="buttonTypeOptions"
              class="min-w-[150px]"
            />
            <input
              v-model="btn.text"
              class="flex-1 min-w-[120px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.templates.buttonText')"
            />
            <input
              v-if="btn.type === 'URL'"
              v-model="btn.url"
              class="flex-1 min-w-[140px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              placeholder="https://…"
            />
            <input
              v-if="btn.type === 'PHONE_NUMBER'"
              v-model="btn.phone_number"
              class="flex-1 min-w-[140px] px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              placeholder="+55…"
            />
            <button
              type="button"
              class="text-n-ruby-11 hover:text-n-ruby-10"
              @click="removeButton(i)"
            >
              ✕
            </button>
          </div>
        </div>

        <div v-if="error" class="text-sm text-n-ruby-11">{{ error }}</div>

        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            :disabled="saving"
            @click="submit"
          >
            {{ saving ? t('DISPARADOR.templates.saving') : t('DISPARADOR.templates.submit', { n: selected.size }) }}
          </button>
          <button
            class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="resetForm"
          >
            {{ t('DISPARADOR.templates.clear') }}
          </button>
        </div>

        <!-- resultados por caixa -->
        <div v-if="results.length" class="mt-2 space-y-1">
          <div
            v-for="r in results"
            :key="r.inbox_id"
            class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border"
            :class="r.success ? 'border-n-teal-6 bg-n-teal-2' : 'border-n-ruby-6 bg-n-ruby-2'"
          >
            <span :class="r.success ? 'text-n-teal-11' : 'text-n-ruby-11'">{{
              r.success ? '✓' : '✕'
            }}</span>
            <span class="font-medium text-n-slate-12">{{ r.inbox_name }}</span>
            <span v-if="!r.success" class="text-xs text-n-ruby-11">{{ r.error }}</span>
            <span v-else class="text-xs text-n-teal-11">{{
              t('DISPARADOR.templates.created')
            }}</span>
          </div>
        </div>
      </div>

      <!-- preview -->
      <div class="lg:col-span-1">
        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">{{
          t('DISPARADOR.templates.preview')
        }}</label>
        <div class="p-3 rounded-xl bg-[#e5ddd5] dark:bg-n-solid-2">
          <div class="p-3 bg-white rounded-lg shadow-sm dark:bg-n-solid-3">
            <p
              v-if="form.header_format === 'TEXT' && previewHeader"
              class="mb-1 text-sm font-bold text-n-slate-12"
            >
              {{ previewHeader }}
            </p>
            <div
              v-else-if="isMediaHeader"
              class="flex items-center justify-center h-24 mb-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-10"
            >
              <img
                v-if="form.header_media_preview"
                :src="form.header_media_preview"
                class="object-cover w-full h-24 rounded-md"
              />
              <span v-else>{{ form.header_format }}</span>
            </div>
            <p class="text-sm whitespace-pre-wrap text-n-slate-12">
              {{ previewBody || t('DISPARADOR.templates.bodyPlaceholder') }}
            </p>
            <p v-if="form.footer" class="mt-1 text-xs text-n-slate-10">
              {{ form.footer }}
            </p>
            <div
              v-if="form.buttons.length"
              class="pt-2 mt-2 space-y-1 border-t border-n-weak"
            >
              <p
                v-for="(btn, i) in form.buttons"
                :key="`pb${i}`"
                class="text-sm text-center text-n-brand"
              >
                {{ btn.text || t('DISPARADOR.templates.buttonText') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
